import {
  CheckboxVisibility,
  IColumn,
  IDetailsGroupRenderProps,
  IDetailsRowProps,
  IGroup,
  IGroupHeaderProps,
  IObjectWithKey,
} from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPIconButton, IPScrollablePane, IPSearchBox, IPShimmeredDetailsList, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPScrollbarVisibility } from 'baseComponents/ScrollablePane/IPScrollablePane';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { baseTableListRowHoverClasses } from 'components/TableList/BaseTableList';
import { buildSelectableGroupHeaderRenderer } from 'components/TableList/SelectableGroupHeader';
import { ISingleTag, SingleTag } from 'components/Tags/SingleTag';
import { globalFilterDelay } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import {
  deleteIcon,
  downArrowIcon,
  globalFontBoldWeight,
  globalSearchBoxStyles,
  globalStackItemStylesPaddingSceneScroll,
  globalStackTokensGapSmall,
  renameIcon,
  upArrowIcon,
} from 'globalStyles';
import Classification from 'models/classification/classification';
import StringValue from 'models/stringValue/stringValue';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHtmlJSX } from 'utils/html';
import { sortOnNumber } from 'utils/sorting';
import { getRandomId } from 'utils/string';

interface IClassificationListProps {
  classifications: Classification[];
  groupValues?: StringValue[]; //when this is specified, these groups are shown, otherwise the groups are shown of the classifications
  isLoading?: boolean;
  showSearch?: boolean;
  onSelectClassification?: (item: Classification) => void;
  onSelectMultipleClassifications?: (item: Classification[]) => void;
  onSelectGroup?: (item: StringValue | undefined) => void;
  onRemove?: (item: Classification) => void;
  onRenameGroup?: (group: StringValue) => void;
  onRemoveGroup?: (group: StringValue) => void;
  onMoveGroup?: (group: StringValue, up: boolean) => void;
  selectedGroup?: StringValue;
  preSelectedClassifications?: Classification[];
  selectMultiple?: boolean;
  allowMultiplePerGroup?: boolean;
}

const ClassificationList = (props: IClassificationListProps) => {
  const { t } = useTranslation(['translation', 'classifications']);
  const appContext = useContext(AppContext);
  const [classificationsDisplay, setClassificationsDisplay] = useState<Classification[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>();
  const [searchTimer, setSearchTimer] = useState<NodeJS.Timeout | undefined>(undefined);
  const [groups, setGroups] = useState<IGroup[] | undefined>(undefined);
  const [selectionMode] = useState<IPSelectionMode>(
    props.selectMultiple && props.onSelectMultipleClassifications
      ? IPSelectionMode.multiple
      : props.onSelectClassification
      ? IPSelectionMode.single
      : IPSelectionMode.none,
  );

  const [selection] = useState<IPSelection<Classification>>(
    new IPSelection({
      selectionMode: selectionMode,
      getKey: (item: Classification) => {
        return item.classificationId;
      },
      onItemsChanged: () => {
        // Called when the items are changed.
        if (props.preSelectedClassifications) {
          const preSelectedIds = props.preSelectedClassifications.map((c) => c.classificationId.toString());
          preSelectedIds.forEach((id) => selection.toggleKeySelected(id));
        }
      },

      onSelectionChanged: () => {
        const sel = selection.getSelection() as Classification[];

        // Select one or more classifications, or only select one.
        if (props.onSelectMultipleClassifications) {
          props.onSelectMultipleClassifications(sel);
        } else if (props.onSelectClassification) {
          props.onSelectClassification(sel[0]);
        }
      },
    }),
  );

  //
  // Effects
  //
  useEffect(() => {
    calcGroups(props.classifications, searchQuery, undefined);
    setClassificationsDisplay(props.classifications);

    //clean-up
    return () => {
      if (searchTimer) {
        clearTimeout(searchTimer);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setClassificationsDisplay(props.classifications);
      calcGroups(props.classifications, searchQuery, selection.getSelection()[0]);
    } else {
      const queryLower = searchQuery.toLowerCase();
      const filtered = props.classifications.filter((entry) => {
        return Object.values(entry).some((val) => {
          if (typeof val === 'string') {
            if (entry.group.value && entry.group.value.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.group.description && entry.group.description.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.name && entry.name.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.description && entry.description.toLowerCase().includes(queryLower)) {
              return true;
            }
            if (entry.examples && entry.examples.toLowerCase().includes(queryLower)) {
              return true;
            }
          }

          return false;
        });
      });

      if (filtered) {
        setClassificationsDisplay(filtered);
        calcGroups(filtered, searchQuery, selection.getSelection()[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.classifications, props.selectedGroup, searchQuery]);

  //
  // Groups
  //
  const calcGroups = (
    classifications: Classification[],
    searchText: string | undefined,
    selectedClassification?: Classification,
  ) => {
    classifications.sort((a, b) => Classification.sortOnGroup(a, b));
    setGroups(getGroups(classifications, searchText, selectedClassification));
  };

  const getGroups = (
    classifications: Classification[],
    searchText: string | undefined,
    selectedClassification?: Classification,
  ): IGroup[] | undefined => {
    try {
      let start: number = 0;
      let uniqueGroups: number[] = [];
      if (props.groupValues) {
        uniqueGroups = [...props.groupValues]
          .sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder))
          .map((g) => g.stringValueId);
      } else {
        uniqueGroups = [...new Set(classifications.map((c) => c.group.stringValueId))];
      }

      const groups = uniqueGroups.map((groupId) => {
        const classificationsGroups = classifications.filter((l) => l.group.stringValueId === groupId);
        const count = classificationsGroups.length;
        const groupName = props.groupValues
          ? props.groupValues.find((g) => g.stringValueId === groupId)?.value ?? 'Unknown'
          : classificationsGroups[0].group.value;
        const group: IGroup = {
          key: groupId.toString(),
          name: groupName,
          startIndex: start,
          count: count,
          level: 0,
          isDropEnabled: true,
          isCollapsed:
            (searchText && count > 0) ||
            selectedClassification?.group.stringValueId === groupId ||
            props.selectedGroup?.stringValueId === groupId
              ? false
              : true,
        };
        start += count;

        return group;
      });

      return groups;
    } catch (err) {
      appContext.setError(err);
    }
  };

  const hasGroupActions: boolean =
    props.groupValues !== undefined &&
    (props.onRenameGroup !== undefined || props.onRemoveGroup !== undefined || props.onMoveGroup !== undefined);

  const getGroupProps = (): IDetailsGroupRenderProps | undefined => {
    return {
      showEmptyGroups: true,
      isAllGroupsCollapsed: true,
      onRenderHeader: buildSelectableGroupHeaderRenderer({
        isGroupSelected: (group) =>
          props.selectedGroup !== undefined && props.selectedGroup.stringValueId.toString() === group.key,
        onGroupSelect: onGroupClick,
      }),
      headerProps: {
        onRenderName: hasGroupActions ? onRenderGroupName : undefined,
        isCollapsedGroupSelectVisible: selectionMode === IPSelectionMode.single ? true : false,
      },
    };
  };

  const onGroupClick = (group: IGroup) => {
    const id = Number.parseInt(group.key);
    if (props.groupValues) {
      const groupValue = props.groupValues.find((g) => g.stringValueId === id);
      if (groupValue && props.onSelectGroup) {
        // group and row selection are mutually exclusive: the command bar and group actions always
        // act on exactly one visible selection
        selection.setAllSelected(false);
        props.onSelectGroup(groupValue);
      }
    } else {
      const classificationGroup = props.classifications.find((l) => l.group.stringValueId === id);
      if (classificationGroup) {
        calcGroups(props.classifications, searchQuery, classificationGroup);
        if (props.onSelectGroup) props.onSelectGroup(classificationGroup.group);
      }
    }
  };

  const onRenderGroupName = (
    headerProps?: IGroupHeaderProps,
    defaultRender?: (p?: IGroupHeaderProps) => JSX.Element | null,
  ): JSX.Element | null => {
    const group = headerProps?.group;
    const groupValue = props.groupValues?.find((g) => g.stringValueId === Number.parseInt(group?.key ?? ''));
    if (!group || !groupValue || !props.groupValues) return defaultRender ? defaultRender(headerProps) : null;

    // position in display order (getGroups sorts on sortOrder the same way) for first/last disabling
    const sortedGroups = [...props.groupValues].sort((a, b) => sortOnNumber(a.sortOrder, b.sortOrder));
    const index = sortedGroups.findIndex((g) => g.stringValueId === groupValue.stringValueId);

    return (
      <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText styles={{ root: { fontWeight: globalFontBoldWeight } }}>{`${group.name} (${group.count})`}</IPText>
        </IPStack.Item>
        <IPStack.Item
          className={`showOnRowHover ${baseTableListRowHoverClasses.action}`}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <IPStack horizontal tokens={globalStackTokensGapSmall}>
            {props.onMoveGroup && (
              <IPTooltipHost content={t('classifications:CommandBar.GroupUp')}>
                <IPIconButton
                  iconProps={upArrowIcon}
                  disabled={index === 0}
                  onClick={() => props.onMoveGroup?.(groupValue, true)}
                />
              </IPTooltipHost>
            )}
            {props.onMoveGroup && (
              <IPTooltipHost content={t('classifications:CommandBar.GroupDown')}>
                <IPIconButton
                  iconProps={downArrowIcon}
                  disabled={index === sortedGroups.length - 1}
                  onClick={() => props.onMoveGroup?.(groupValue, false)}
                />
              </IPTooltipHost>
            )}
            {props.onRenameGroup && (
              <IPTooltipHost content={t('classifications:CommandBar.GroupRename')}>
                <IPIconButton iconProps={renameIcon} onClick={() => props.onRenameGroup?.(groupValue)} />
              </IPTooltipHost>
            )}
            {props.onRemoveGroup && (
              <IPTooltipHost content={t('classifications:CommandBar.GroupRemove')}>
                <IPIconButton iconProps={deleteIcon} onClick={() => props.onRemoveGroup?.(groupValue)} />
              </IPTooltipHost>
            )}
          </IPStack>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Columns
  //
  const getColumns = (): IColumn[] => {
    const columns: IColumn[] = [
      {
        key: 'value',
        name: t('classifications:Columns.Value'),
        minWidth: 80,
        maxWidth: 80,
        isResizable: true,
        onRender: (item?: Classification) => {
          if (!item) return;

          return <IPText>{item?.value}</IPText>;
        },
      },
      {
        key: 'valueName',
        name: t('classifications:Columns.ValueName'),
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        onRender: (item?: Classification) => {
          if (!item) return;

          const tag: ISingleTag = {
            tagId: getRandomId(),
            tagName: item.name,
            tagColor: item.color,
          };

          return <SingleTag tag={tag} />;
        },
      },
      {
        key: 'description',
        name: t('classifications:Columns.Description'),
        minWidth: 100,
        maxWidth: 300,
        isResizable: true,
        isMultiline: true,
        isCollapsible: true,
        onRender: (item?: Classification) => {
          if (!item) return;

          return <div>{getHtmlJSX(item.description, true)}</div>;
        },
      },
      {
        key: 'examples',
        name: t('classifications:Columns.Examples'),
        minWidth: 100,
        maxWidth: 300,
        isResizable: true,
        isMultiline: true,
        isCollapsible: true,
        onRender: (item?: Classification) => {
          if (!item) return;

          return <div>{getHtmlJSX(item.examples, true)}</div>;
        },
      },
    ];

    if (props.onRemove) {
      const removeColumn: IColumn = {
        key: 'remove',
        name: t('classifications:Columns.Remove'),
        minWidth: 100,
        maxWidth: 100,
        onRender: (item?: Classification) => {
          return (
            <IPStack horizontal horizontalAlign="center">
              <IPIconButton
                iconProps={deleteIcon}
                onClick={() => {
                  if (!item || !props.onRemove) return;
                  props.onRemove(item);
                }}
                styles={{ root: { height: 18 } }}
              />
            </IPStack>
          );
        },
      };

      columns.push(removeColumn);
    }

    return columns;
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
      calcGroups(props.classifications, filterText, selection.getSelection()[0]);
    } else {
      setSearchQuery('');
      calcGroups(props.classifications, undefined, selection.getSelection()[0]);
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {props.showSearch && (
        <IPStack.Item>
          <IPSearchBox
            autoFocus={!appContext.isMobileView}
            underlined
            placeholder={t('translation:General.Filter.Placeholder')}
            id="search"
            onChange={(ev, newValue) => {
              if (searchTimer) {
                clearTimeout(searchTimer);
              }
              setSearchTimer(setTimeout(() => onChangeText(ev, newValue), globalFilterDelay));
            }}
            styles={globalSearchBoxStyles}
          />
        </IPStack.Item>
      )}
      <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
        <IPScrollablePane scrollbarVisibility={IPScrollbarVisibility.auto}>
          <IPShimmeredDetailsList
            selectionZoneProps={{
              // We prevent selection of the whole row. Only the checkbox is selectable.
              // This is to prevent group selection, as we only want one classification per group to be selectable.
              selectionClearedOnSurfaceClick: selectionMode === IPSelectionMode.single ? true : false,
            }}
            // We hide all checkboxes. Then, in onRenderRow, we enable the showing of row checkboxes.
            // This hides the group header and column header checkboxes.
            checkboxVisibility={
              selectionMode === IPSelectionMode.single ? CheckboxVisibility.always : CheckboxVisibility.hidden
            }
            compact
            enableShimmer={props.isLoading}
            selection={selection as IPSelection<IObjectWithKey>}
            items={classificationsDisplay}
            isHeaderVisible={true}
            columns={getColumns()}
            setKey="set"
            getKey={(item: Classification) => item.classificationId.toString()}
            selectionMode={selectionMode}
            layoutMode={IPDetailsListLayoutMode.justified}
            groups={groups}
            groupProps={getGroupProps()}
            selectionPreservedOnEmptyClick={true}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            onRenderRow={(detailRowProps: IDetailsRowProps | undefined, defaultRender) => {
              // Exit early if single select is enabled.
              if (selectionMode === IPSelectionMode.single) {
                return defaultRender ? defaultRender(detailRowProps) : null;
              }

              if (detailRowProps) {
                const classification = detailRowProps.item as Classification;
                const sel = selection.getSelection() as Classification[];

                // If the current item belongs to a group that already has a selected member, disable the checkbox.
                // This is to prevent multiple selections from the same group.
                const alreadySelected = sel.find(
                  (s) =>
                    s.group.stringValueId === classification.group.stringValueId &&
                    s.classificationId !== classification.classificationId,
                );
                if (alreadySelected && !props.allowMultiplePerGroup) {
                  detailRowProps.disabled = true;
                }

                // Checkboxes are disabled in the general props, so we enable them here.
                detailRowProps.checkboxVisibility = CheckboxVisibility.always;
              }

              return defaultRender ? defaultRender(detailRowProps) : null;
            }}
          />
        </IPScrollablePane>
      </IPStack.Item>
    </IPStack>
  );
};

export default ClassificationList;
